// @oasis-permission-inherits: /jerboa-client-network-load (jerboa:view)
import { Link, createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useMemo } from "react";
import { z } from "zod";

import {
  InfiniteQueryPagination,
  useInfiniteQueryPagination,
} from "~/components/infinite-query-pagination";
import { Badge } from "~/components/ui/badge";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  DurationChart,
  SuccessRateChart,
} from "~/modules/jerboa-client-network-load/components/metrics-chart";
import {
  formatBytes,
  formatDurationMs,
  formatMbps,
  formatTimestamp,
  statusVariant,
} from "~/modules/jerboa-client-network-load/formatters";
import {
  fetchJerboaClientNetworkLoadAnalysisQuery,
  fetchJerboaClientNetworkLoadsQuery,
} from "~/modules/jerboa-client-network-load/queries";
import {
  jerboaClientNetworkLoadContextSchema,
  jerboaClientNetworkLoadStatusSchema,
  type JerboaClientNetworkLoadContext,
  type JerboaClientNetworkLoadStatus,
  type NetworkLoadAnalysisBucket,
} from "~/modules/jerboa-client-network-load/schemas";

const metricDaysOptions = [1, 7, 30] as const;
type MetricDays = (typeof metricDaysOptions)[number];

const searchSchema = z.object({
  page: z.number().optional(),
  status: jerboaClientNetworkLoadStatusSchema.optional(),
  context: jerboaClientNetworkLoadContextSchema.optional(),
  projectId: z.string().optional(),
  metricDays: z.union([z.literal(1), z.literal(7), z.literal(30)]).optional(),
});

export const Route = createFileRoute("/_dash/jerboa-client-network-load/")({
  validateSearch: searchSchema,
  component: RouteComponent,
});

interface NetworkLoadMetricsProps {
  isLoading: boolean;
  error: Error | null;
  buckets: NetworkLoadAnalysisBucket[] | undefined;
  children: (buckets: NetworkLoadAnalysisBucket[]) => React.ReactNode;
}

function NetworkLoadMetrics({ isLoading, error, buckets, children }: NetworkLoadMetricsProps) {
  if (isLoading) {
    return <Skeleton className="h-[200px] w-full rounded-md" />;
  }

  if (error) {
    return (
      <div className="flex h-[200px] items-center justify-center rounded-md border border-destructive bg-destructive/10 p-4 text-center text-sm text-destructive">
        <div>
          <p className="font-medium">Failed to load network metrics</p>
          <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
        </div>
      </div>
    );
  }

  if (!buckets || buckets.length === 0) {
    return (
      <div className="flex h-[200px] items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
        No network metrics found for this period
      </div>
    );
  }

  return <>{children(buckets)}</>;
}

function RouteComponent() {
  const { page, status, context, projectId, metricDays } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const router = useRouter();
  const selectedMetricDays = metricDays ?? 7;

  const params = useMemo(() => ({ status, context, projectId }), [status, context, projectId]);
  const searchParams = useMemo(
    () => ({ status, context, projectId, metricDays }),
    [status, context, projectId, metricDays]
  );

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, error } =
    fetchJerboaClientNetworkLoadsQuery.useInfiniteQuery({
      params,
    });

  const {
    data: analysisData,
    isLoading: isAnalysisLoading,
    error: analysisError,
  } = fetchJerboaClientNetworkLoadAnalysisQuery.useQuery({
    params: { days: selectedMetricDays },
  });

  const {
    currentPageIndex,
    currentPageData,
    totalPages,
    onPreviousPage,
    onNextPage,
    onPageChange,
  } = useInfiniteQueryPagination({
    page,
    data,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    navigateToPage: (nextPage) => navigate({ search: { ...searchParams, page: nextPage } }),
  });

  const onStatusChange = (nextStatus: JerboaClientNetworkLoadStatus | undefined) => {
    navigate({ search: { page: 1, projectId, context, metricDays, status: nextStatus } });
  };

  const onContextChange = (nextContext: JerboaClientNetworkLoadContext | undefined) => {
    navigate({ search: { page: 1, projectId, status, metricDays, context: nextContext } });
  };

  const onMetricDaysChange = (nextMetricDays: MetricDays) => {
    navigate({ search: { ...params, page, metricDays: nextMetricDays } });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Client Network</h1>
        {totalPages > 0 && (
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        )}
      </div>

      <div className="rounded-md border bg-muted/20 p-4">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium">
              Editor load time over the last {selectedMetricDays}{" "}
              {selectedMetricDays === 1 ? "day" : "days"}
            </p>
            <p className="text-xs text-muted-foreground">
              Mount → ready · median / p90 / p95 — 2-hour buckets
            </p>
          </div>
          <Tabs
            value={String(selectedMetricDays)}
            onValueChange={(value) => onMetricDaysChange(Number(value) as MetricDays)}
          >
            <TabsList className="h-8">
              {metricDaysOptions.map((days) => (
                <TabsTrigger key={days} value={String(days)} className="text-xs">
                  {days}d
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <NetworkLoadMetrics
          isLoading={isAnalysisLoading}
          error={analysisError}
          buckets={analysisData?.analysis.buckets}
        >
          {(buckets) => <DurationChart buckets={buckets} />}
        </NetworkLoadMetrics>
      </div>

      <div className="rounded-md border bg-muted/20 p-4">
        <div className="mb-3">
          <p className="text-sm font-medium">
            Editor load success rate over the last {selectedMetricDays}{" "}
            {selectedMetricDays === 1 ? "day" : "days"}
          </p>
          <p className="text-xs text-muted-foreground">
            finishCount / totalCount per 2-hour bucket
          </p>
        </div>
        <NetworkLoadMetrics
          isLoading={isAnalysisLoading}
          error={analysisError}
          buckets={analysisData?.analysis.buckets}
        >
          {(buckets) => <SuccessRateChart buckets={buckets} />}
        </NetworkLoadMetrics>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Status:</span>
          {(["finish", "error"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onStatusChange(status === s ? undefined : s)}
              className="inline-flex"
            >
              <Badge variant={status === s ? statusVariant(s) : "outline"}>{s}</Badge>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Context:</span>
          {(["preview", "export"] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onContextChange(context === c ? undefined : c)}
              className="inline-flex"
            >
              <Badge variant={context === c ? "default" : "outline"}>{c}</Badge>
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Failed to load network load results</p>
          <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
        </div>
      )}

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      )}

      {!isLoading && currentPageData && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Created At</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Context</TableHead>
              <TableHead>Project ID</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Assets</TableHead>
              <TableHead>Bytes</TableHead>
              <TableHead>Observed Mbps</TableHead>
              <TableHead>Version</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentPageData.page.map((result) => {
              const totalBytes = result.assetBytesFromNetwork + result.assetBytesFromCache;
              return (
                <TableRow
                  key={result.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() =>
                    router.navigate({
                      to: "/jerboa-client-network-load/$id",
                      params: { id: result.id },
                      search: { page: currentPageIndex + 1, ...searchParams },
                    })
                  }
                >
                  <TableCell>{formatTimestamp(result.createdAt)}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(result.status)}>{result.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{result.context}</Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {result.projectId ? (
                      <Link
                        to="/projects/$projectId"
                        params={{ projectId: result.projectId }}
                        className="text-blue-600 hover:underline"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {result.projectId}
                      </Link>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>{formatDurationMs(result.totalDurationMs)}</TableCell>
                  <TableCell>{result.assetCount}</TableCell>
                  <TableCell className="font-mono text-xs">{formatBytes(totalBytes)}</TableCell>
                  <TableCell>{formatMbps(result.networkObservedMbps)}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {result.appVersion ? (
                      <a
                        href={`https://github.com/bungeeapp/jerboa/commit/${result.appVersion}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {result.appVersion.slice(0, 7)}
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {currentPageData.page.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-muted-foreground">
                  No network load results found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {totalPages > 0 && !isLoading && !isFetchingNextPage && (
        <div className="flex justify-end">
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        </div>
      )}
    </div>
  );
}
